import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { usePublicApplicationForm, FIXED_FIELDS } from '../hooks/useApplicationForm';
import { useAuthContext } from '../contexts/AuthContext';
import { useAuth } from '../hooks/useAuth';

// ── フィールドレンダラー ──────────────────────────────────────────────────
const FieldInput = ({ field, value, onChange, error, readOnly }) => {
  const base = {
    width: '100%', padding: '10px 12px', borderRadius: 10, boxSizing: 'border-box',
    border: `1.5px solid ${error ? '#fca5a5' : readOnly ? '#e0e7ff' : '#e2e8f0'}`,
    background: error ? '#fff5f5' : readOnly ? '#f5f3ff' : '#fff',
    fontSize: 14, outline: 'none', transition: 'border-color 0.15s',
    color: readOnly ? '#4c1d95' : '#0f172a',
  };

  if (readOnly) {
    return <div style={{ ...base, display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 11, color: '#6c63ff', fontWeight: 700, flexShrink: 0 }}>🔒</span>
      <span>{value || '—'}</span>
    </div>;
  }

  if (field.inputType === 'textarea') return (
    <textarea rows={3} value={value} onChange={e => onChange(e.target.value)}
      placeholder={field.placeholder || ''} style={{ ...base, resize: 'vertical', fontFamily: 'inherit' }} />
  );

  if (field.inputType === 'select') return (
    <select value={value} onChange={e => onChange(e.target.value)} style={{ ...base }}>
      <option value="">選択してください</option>
      {(field.options || []).map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
    </select>
  );

  if (field.inputType === 'radio') return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {(field.options || []).map((opt, i) => (
        <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#334155' }}>
          <input type="radio" name={field.id} value={opt} checked={value === opt} onChange={() => onChange(opt)}
            style={{ width: 16, height: 16 }} />
          {opt}
        </label>
      ))}
    </div>
  );

  if (field.inputType === 'checkbox') {
    const vals = Array.isArray(value) ? value : [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {(field.options || []).map((opt, i) => (
          <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#334155' }}>
            <input type="checkbox" checked={vals.includes(opt)}
              onChange={e => onChange(e.target.checked ? [...vals, opt] : vals.filter(v => v !== opt))}
              style={{ width: 16, height: 16 }} />
            {opt}
          </label>
        ))}
      </div>
    );
  }

  return (
    <input type="text" value={value} onChange={e => onChange(e.target.value)}
      placeholder={field.placeholder || ''} style={base} />
  );
};

// ── メイン ───────────────────────────────────────────────────────────────
const ApplicationFormPage = () => {
  const { token } = useParams();
  const { loading, invalid, event, formDef, submitApplication } = usePublicApplicationForm(token);
  const { user, userProfile } = useAuthContext();
  const { login, registerGuest, loading: authOpLoading, error: authError } = useAuth();

  const [values, setValues]     = useState({});
  const [errors, setErrors]     = useState({});
  const [status, setStatus]     = useState('idle'); // idle | submitting | success | duplicate | error

  // アカウントセクションの状態
  const [authMode, setAuthMode] = useState('none'); // none | login | register
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [authDone, setAuthDone] = useState(false); // ログイン/登録完了フラグ
  const [authErrMsg, setAuthErrMsg] = useState('');

  // ログイン済みの場合、標準フィールドをプロフィールで埋める
  useEffect(() => {
    if (!user || !userProfile) return;
    setValues(prev => {
      const next = { ...prev };
      if (userProfile.vrchat_name    && !next['_vrchat_name'])    next['_vrchat_name']    = userProfile.vrchat_name;
      if (userProfile.vrchat_profile && !next['_vrchat_profile']) next['_vrchat_profile'] = userProfile.vrchat_profile;
      if (userProfile.x_url          && !next['_x_url'])          next['_x_url']          = userProfile.x_url;
      if (userProfile.discord        && !next['_discord'])        next['_discord']        = userProfile.discord;
      return next;
    });
  }, [user, userProfile]);

  if (loading) return (
    <div style={centerStyle}>
      <div style={{ fontSize: 40 }}>⏳</div>
      <p style={{ color: '#94a3b8', marginTop: 12 }}>読み込み中...</p>
    </div>
  );

  if (invalid) return (
    <div style={centerStyle}>
      <div style={{ fontSize: 48 }}>🚫</div>
      <h2 style={{ color: '#0f172a', margin: '16px 0 8px', fontWeight: 900 }}>受付終了</h2>
      <p style={{ color: '#64748b', fontSize: 15, textAlign: 'center', maxWidth: 320 }}>
        このフォームは現在受付を停止しています。<br />イベントの情報をご確認ください。
      </p>
    </div>
  );

  if (status === 'success') return (
    <div style={centerStyle}>
      <div style={{ fontSize: 56 }}>🎉</div>
      <h2 style={{ color: '#0f172a', margin: '16px 0 8px', fontWeight: 900 }}>応募完了！</h2>
      <p style={{ color: '#64748b', fontSize: 15, textAlign: 'center', maxWidth: 320 }}>
        応募が受け付けられました。<br />抽選結果をお待ちください。
      </p>
      {user && (
        <Link to="/portal" style={{ marginTop: 20, display: 'inline-block', padding: '10px 24px', borderRadius: 10, background: 'linear-gradient(135deg, #6c63ff, #8b5cf6)', color: '#fff', fontWeight: 700, textDecoration: 'none', fontSize: 14 }}>
          ポータルで応募状況を確認 →
        </Link>
      )}
    </div>
  );

  if (status === 'duplicate') return (
    <div style={centerStyle}>
      <div style={{ fontSize: 48 }}>⚠️</div>
      <h2 style={{ color: '#0f172a', margin: '16px 0 8px', fontWeight: 900 }}>すでに登録済みです</h2>
      <p style={{ color: '#64748b', fontSize: 15, textAlign: 'center', maxWidth: 360 }}>
        入力されたVRChat表示名はすでにこのイベントに登録されています。<br />
        変更が必要な場合はイベント運営者にご連絡ください。
      </p>
    </div>
  );

  // フォームの全フィールド（固定 + 追加）。fields が空配列でも FIXED_FIELDS にフォールバック
  const allFields = formDef?.fields?.length > 0 ? formDef.fields : [...FIXED_FIELDS];

  // 標準フィールドがプロフィールで埋まっているか判定（read-only にするか）
  const isStandardFieldLocked = (field) => {
    if (!user || !userProfile) return false;
    const profileMap = {
      '_vrchat_name':    userProfile.vrchat_name,
      '_vrchat_profile': userProfile.vrchat_profile,
      '_x_url':          userProfile.x_url,
      '_discord':        userProfile.discord,
    };
    return Boolean(profileMap[field.id]);
  };

  const validate = () => {
    const errs = {};
    allFields.forEach(f => {
      if (!f.required) return;
      const v = values[f.id] ?? values[f.key] ?? '';
      if (!v || (Array.isArray(v) && v.length === 0) || String(v).trim() === '') {
        errs[f.id] = true;
      }
    });
    return errs;
  };

  const handleAuthAction = async () => {
    setAuthErrMsg('');
    if (!email || !password) { setAuthErrMsg('メールアドレスとパスワードを入力してください'); return; }

    if (authMode === 'login') {
      const ok = await login(email, password);
      if (!ok) {
        setAuthErrMsg('メールアドレスまたはパスワードが正しくありません');
      } else {
        setAuthDone(true);
      }
    } else {
      // register
      try {
        const getVal = (key) => values[key] ?? '';
        await registerGuest(email, password, {
          vrchat_name:    getVal('_vrchat_name'),
          vrchat_profile: getVal('_vrchat_profile'),
          x_url:          getVal('_x_url') || null,
          discord:        getVal('_discord') || null,
        });
        setAuthDone(true);
      } catch (e) {
        if (e.message === 'auth/email-already-in-use') {
          setAuthErrMsg('このメールアドレスはすでに登録されています。ログインを選択してください。');
        } else {
          setAuthErrMsg('アカウントの作成に失敗しました。もう一度お試しください。');
        }
      }
    }
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setStatus('submitting');
    try {
      const getVal = (field) => values[field.id] ?? values[field.key] ?? '';
      await submitApplication({
        vrchat_name:    getVal(allFields.find(f => f.key === 'vrchat_name') || {}),
        vrchat_profile: getVal(allFields.find(f => f.key === 'vrchat_profile') || {}),
        x_url:          getVal(allFields.find(f => f.key === 'x_url') || {}),
        discord:        getVal(allFields.find(f => f.key === 'discord') || {}),
        image_url:      userProfile?.image_url || null,
        user_id:        user?.uid || null,
        answers: Object.fromEntries(
          allFields.filter(f => f.type === 'custom').map(f => [f.id, values[f.id] ?? ''])
        ),
      });
      setStatus('success');
    } catch (e) {
      setStatus(e.message === 'duplicate' ? 'duplicate' : 'error');
    }
  };

  const hasLockedFields = user && userProfile && (
    userProfile.vrchat_name || userProfile.vrchat_profile || userProfile.x_url || userProfile.discord
  );

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f3ff 0%, #e0f2fe 100%)', display: 'flex', justifyContent: 'center', padding: '32px 16px 64px' }}>
      <div style={{ width: '100%', maxWidth: 520 }}>

        {/* ヘッダー */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: '#6c63ff', background: 'rgba(108,99,255,0.08)', padding: '5px 14px', borderRadius: 20, marginBottom: 14, border: '1px solid rgba(108,99,255,0.15)' }}>
            <img src="/logo.png" alt="LinkBoard" style={{ height: 16, width: 'auto' }} />
            LinkBoard
          </div>
          <h1 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 800, color: '#6c63ff' }}>
            {event?.event_name}
          </h1>
          {formDef?.title && formDef.title !== event?.event_name && (
            <p style={{ margin: '0 0 6px', fontSize: 26, fontWeight: 900, color: '#0f172a', lineHeight: 1.3 }}>{formDef.title}</p>
          )}
          {formDef?.description ? (
            <p style={{ margin: '6px 0 0', fontSize: 14, color: '#475569', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{formDef.description}</p>
          ) : (
            <p style={{ margin: 0, fontSize: 14, color: '#94a3b8' }}>応募フォーム</p>
          )}
        </div>

        {/* アカウントセクション（未ログイン時のみ・フォームの上） */}
        {!user && (
          <div style={{ marginBottom: 16 }}>
            {authMode === 'none' && !authDone && (
              <div style={{ padding: '14px 16px', borderRadius: 12, background: '#f5f3ff', border: '1px solid #c4b5fd' }}>
                <p style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700, color: '#4c1d95' }}>
                  アカウントと連携しますか？（任意）
                </p>
                <p style={{ margin: '0 0 12px', fontSize: 11, color: '#6c63ff', lineHeight: 1.6 }}>
                  アカウントを登録すると、次回から情報が自動入力されます。また抽選結果をポータルで確認できます。
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setAuthMode('register')} style={btnOutline('#6c63ff')}>
                    新規登録
                  </button>
                  <button onClick={() => setAuthMode('login')} style={btnOutline('#6c63ff')}>
                    ログイン
                  </button>
                </div>
              </div>
            )}

            {(authMode === 'login' || authMode === 'register') && !authDone && (
              <div style={{ padding: '16px 18px', borderRadius: 12, background: '#f5f3ff', border: '1px solid #c4b5fd' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 800, color: '#4c1d95' }}>
                    {authMode === 'login' ? 'ログイン' : '新規アカウント登録'}
                  </p>
                  <button onClick={() => { setAuthMode('none'); setAuthErrMsg(''); }} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 18, cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>メールアドレス</label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                      placeholder="example@email.com"
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #c4b5fd', fontSize: 13, outline: 'none', boxSizing: 'border-box', background: '#fff' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>パスワード</label>
                    <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                      placeholder={authMode === 'register' ? '6文字以上' : 'パスワードを入力'}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #c4b5fd', fontSize: 13, outline: 'none', boxSizing: 'border-box', background: '#fff' }} />
                  </div>
                  {(authErrMsg || authError) && (
                    <p style={{ margin: 0, fontSize: 11, color: '#dc2626', fontWeight: 600 }}>{authErrMsg || authError}</p>
                  )}
                  <button onClick={handleAuthAction} disabled={authOpLoading}
                    style={{ padding: '10px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #6c63ff, #8b5cf6)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: authOpLoading ? 'not-allowed' : 'pointer', opacity: authOpLoading ? 0.7 : 1 }}>
                    {authOpLoading ? '処理中...' : (authMode === 'login' ? 'ログインする' : 'アカウントを作成する')}
                  </button>
                  {authMode === 'login' && (
                    <p style={{ margin: 0, fontSize: 11, color: '#6c63ff', textAlign: 'center', cursor: 'pointer' }}
                      onClick={() => { setAuthMode('register'); setAuthErrMsg(''); }}>
                      アカウントをお持ちでない方はこちら
                    </p>
                  )}
                  {authMode === 'register' && (
                    <p style={{ margin: 0, fontSize: 11, color: '#6c63ff', textAlign: 'center', cursor: 'pointer' }}
                      onClick={() => { setAuthMode('login'); setAuthErrMsg(''); }}>
                      すでにアカウントをお持ちの方はこちら
                    </p>
                  )}
                </div>
              </div>
            )}

            {authDone && (
              <div style={{ padding: '12px 16px', borderRadius: 10, background: '#f0fdf4', border: '1px solid #86efac', fontSize: 13, color: '#166534', fontWeight: 600 }}>
                ✓ アカウントと連携しました。このまま応募を完了してください。
              </div>
            )}
          </div>
        )}

        {/* ログイン済みバナー */}
        {user && userProfile && (
          <div style={{ marginBottom: 16, padding: '10px 16px', borderRadius: 10, background: '#f0fdf4', border: '1px solid #86efac', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#166534' }}>
                ✓ アカウント情報で応募します
              </p>
              <p style={{ margin: '2px 0 0', fontSize: 11, color: '#4ade80' }}>
                {userProfile.display_name || userProfile.vrchat_name || user.email}
              </p>
            </div>
            <Link to="/account" state={{ from: '/apply' }} style={{ fontSize: 11, color: '#16a34a', textDecoration: 'underline', flexShrink: 0 }}>
              プロフィール設定
            </Link>
          </div>
        )}

        {/* フォームカード */}
        <div style={{ background: '#fff', borderRadius: 20, padding: '28px 28px', boxShadow: '0 8px 32px rgba(0,0,0,0.08)', border: '1px solid #e8edf5' }}>

          {/* ロック済みフィールドの説明 */}
          {hasLockedFields && (
            <div style={{ marginBottom: 18, padding: '10px 14px', borderRadius: 8, background: '#f5f3ff', border: '1px solid #c4b5fd', fontSize: 12, color: '#4c1d95' }}>
              🔒 アカウントのプロフィール情報で自動入力されています。変更は
              <Link to="/account" state={{ from: '/apply' }} style={{ color: '#6c63ff', fontWeight: 700, marginLeft: 2 }}>プロフィール設定</Link>
              から行ってください。
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
            {allFields.map(field => {
              const fieldKey = field.id;
              const locked = isStandardFieldLocked(field);
              const val = values[fieldKey] ?? '';
              const hasErr = errors[fieldKey];
              return (
                <div key={fieldKey}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {field.label}
                    {field.required && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
                    {locked && <span style={{ marginLeft: 6, fontSize: 10, color: '#6c63ff', background: '#ede9fe', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>自動入力</span>}
                  </label>
                  {field.description && (
                    <p style={{ margin: '0 0 6px', fontSize: 12, color: '#94a3b8' }}>{field.description}</p>
                  )}
                  <FieldInput
                    field={field} value={val}
                    onChange={v => setValues(prev => ({ ...prev, [fieldKey]: v }))}
                    error={hasErr}
                    readOnly={locked}
                  />
                  {hasErr && (
                    <p style={{ margin: '4px 0 0', fontSize: 11, color: '#ef4444', fontWeight: 600 }}>この項目は必須です</p>
                  )}
                </div>
              );
            })}
          </div>

          {/* エラー */}
          {status === 'error' && (
            <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fca5a5', fontSize: 13, color: '#dc2626', fontWeight: 600 }}>
              送信に失敗しました。もう一度お試しください。
            </div>
          )}

          {/* 送信ボタン */}
          <button
            onClick={handleSubmit}
            disabled={status === 'submitting'}
            style={{
              width: '100%', marginTop: 24, padding: '13px',
              borderRadius: 12, border: 'none',
              background: status === 'submitting' ? '#a5b4fc' : 'linear-gradient(135deg, #6c63ff, #8b5cf6)',
              color: '#fff', fontSize: 15, fontWeight: 800, cursor: status === 'submitting' ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 16px rgba(108,99,255,0.35)',
              transition: 'all 0.15s',
            }}>
            {status === 'submitting' ? '送信中...' : '応募する →'}
          </button>
        </div>

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: 12, color: '#94a3b8' }}>
          Powered by{' '}
          <a href={window.location.origin} target="_blank" rel="noreferrer"
            style={{ color: '#6c63ff', fontWeight: 700, textDecoration: 'none' }}>
            LinkBoard
          </a>
        </p>
      </div>
    </div>
  );
};

const btnOutline = (color) => ({
  flex: 1, padding: '8px 0', borderRadius: 8, border: `1.5px solid ${color}`,
  background: '#fff', color: color, fontSize: 13, fontWeight: 700, cursor: 'pointer',
});

const centerStyle = {
  minHeight: '100vh', display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center', padding: 24,
  background: 'linear-gradient(135deg, #f5f3ff 0%, #e0f2fe 100%)',
};

export default ApplicationFormPage;
